import React from 'react';
import { PilotData } from '../types';

interface PilotPopupProps {
  pilot: PilotData;
  onLoadHistory?: (pilotId: string) => void;
  onToggleHistory?: (pilotId: string, show: boolean) => void;
  isHistoryLoading?: boolean;
  hasHistory?: boolean;
  isShowingHistory?: boolean;
  onCenter?: (pilot: PilotData) => void;
  onFollow?: (pilotId: string | null) => void;
  isFollowing?: boolean;
}

const PilotPopup: React.FC<PilotPopupProps> = ({
  pilot,
  onLoadHistory,
  onToggleHistory,
  isHistoryLoading = false,
  hasHistory = false,
  isShowingHistory = false,
  onCenter,
  onFollow,
  isFollowing = false
}) => {
  // Determine flight state based on speed and altitude change
  const getFlightState = () => {
    if (pilot.flightState) return pilot.flightState.toLowerCase();
    if (pilot.speed > 10) return 'flying';
    if (pilot.speed > 2) return 'walking';
    return 'stationary';
  };

  const flightState = getFlightState();

  // Format altitude with proper units
  const formatAltitude = (alt: number) => {
    return `${Math.round(alt)} m`;
  };

  // Format speed
  const formatSpeed = (speed: number) => `${speed.toFixed(1)} km/h`;

  // Format heading
  const formatHeading = (heading: number) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(heading / 45) % 8;
    return `${Math.round(heading)}° ${directions[index]}`;
  };

  // Format time
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="pilot-popup-premium">
      <div className="pilot-popup-header">
        <div
          className="pilot-color-indicator"
          style={{ backgroundColor: pilot.color }}
        />
        <h3>{pilot.name}</h3>
        <div className={`flight-state ${flightState}`}>
          {flightState}
        </div>
      </div>

      <div className="pilot-popup-stats">
        {pilot.flightTime && (
          <div className="stat-row">
            <div className="stat-icon">⏱️</div>
            <div className="stat-label">Flight Time</div>
            <div className="stat-value">{Math.round(pilot.flightTime / 60)} min</div>
          </div>
        )}

        <div className="stat-row">
          <div className="stat-icon">🏔️</div>
          <div className="stat-label">Altitude</div>
          <div className="stat-value">{formatAltitude(pilot.altitude)}</div>
        </div>

        <div className="stat-row">
          <div className="stat-icon">💨</div>
          <div className="stat-label">Speed</div>
          <div className="stat-value">{formatSpeed(pilot.speed)}</div>
        </div>

        {pilot.maxSpeed && (
          <div className="stat-row">
            <div className="stat-icon">🚀</div>
            <div className="stat-label">Max Speed</div>
            <div className="stat-value">{formatSpeed(pilot.maxSpeed)}</div>
          </div>
        )}

        <div className="stat-row">
          <div className="stat-icon">🧭</div>
          <div className="stat-label">Heading</div>
          <div className="stat-value">{formatHeading(pilot.heading)}</div>
        </div>

        <div className="stat-row">
          <div className="stat-icon">🕐</div>
          <div className="stat-label">Last Update</div>
          <div className="stat-value">{formatTime(pilot.timestamp)}</div>
        </div>

        <div className="stat-row">
          <div className="stat-icon">📍</div>
          <div className="stat-label">Position</div>
          <div className="stat-value">
            {pilot.lat.toFixed(5)}, {pilot.lng.toFixed(5)}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pilot-popup-actions">
        <button
          className="action-button action-button-center"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log('Center button clicked for pilot:', pilot.id);
            if (onCenter) {
              onCenter(pilot);
            }
          }}
          title="Center map on pilot"
        >
          <span className="action-icon">📍</span>
          <span>Center</span>
        </button>

        <button
          className={`action-button action-button-follow ${isFollowing ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            console.log('Follow button clicked for pilot:', pilot.id);
            if (onFollow) {
              onFollow(isFollowing ? null : pilot.id);
            }
          }}
          title={isFollowing ? "Stop following" : "Follow pilot"}
        >
          <span className="action-icon">{isFollowing ? '⏹️' : '👁️'}</span>
          <span>{isFollowing ? 'Unfollow' : 'Follow'}</span>
        </button>
      </div>

      {/* History Button */}
      {onLoadHistory && (
        <div className="pilot-popup-history">
          <button
            className="history-button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              console.log('History button clicked for pilot:', pilot.id);

              if (!hasHistory && !isHistoryLoading) {
                // Load history if not already loaded
                console.log('Loading history for pilot:', pilot.id);
                onLoadHistory(pilot.id);
              } else if (hasHistory && onToggleHistory) {
                // Toggle visibility if history is loaded
                const newShowState = !isShowingHistory;
                console.log(`Toggling history visibility to: ${newShowState}`);
                onToggleHistory(pilot.id, newShowState);
              }
            }}
            disabled={isHistoryLoading}
          >
            {isHistoryLoading ? (
              <>
                <span className="loading-spinner"></span>
                <span>Loading History...</span>
              </>
            ) : hasHistory && isShowingHistory ? (
              <span>Hide Track History</span>
            ) : (
              <span>Show Track History</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default PilotPopup;